import { useState, useEffect } from 'react';
import { FaPlus, FaUsers, FaCalendarAlt, FaTrophy, FaChartLine, FaSearch, FaFilter, FaEdit, FaEye, FaTrash, FaKey, FaSyncAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import futbolistaService from '../../services/futbolistaService';
import temporadaService from '../../services/temporadaService';
import TutorLayout from '../../components/tutor/TutorLayout';

const FutbolistaManagement = () => {
  const navigate = useNavigate();
  const [futbolistas, setFutbolistas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: '', variant: 'info' });
  const [temporadaActiva, setTemporadaActiva] = useState(null);

  // Estados para filtros y búsqueda
  const [filtroEdad, setFiltroEdad] = useState('todos');
  const [busqueda, setBusqueda] = useState('');

  useEffect(() => {
    loadFutbolistas();
    loadTemporadaActiva();
  }, []);

  const loadTemporadaActiva = async () => {
    try {
      const response = await temporadaService.getTemporadaActiva();

      if (response.success && response.data) {
        setTemporadaActiva(response.data);
      } else {
        console.warn('No se pudo cargar temporada activa:', response.error);
        setTemporadaActiva(null);
      }
    } catch (error) {
      console.error('Error al cargar temporada activa:', error);
      setTemporadaActiva(null);
    }
  };

  const loadFutbolistas = async () => {
    try {
      setLoading(true);
      const data = await futbolistaService.getFutbolistas();
      setFutbolistas(data || []);
    } catch (error) {
      console.error('Error al cargar futbolistas:', error);
      setAlert({
        show: true,
        message: 'Error al cargar la lista de futbolistas',
        variant: 'danger'
      });
    } finally {
      setLoading(false);
    }
  };

  const calcularEdad = (fechaNacimiento) => {
    if (!fechaNacimiento) return 'N/A';
    const hoy = new Date();
    const nacimiento = new Date(fechaNacimiento);
    let edad = hoy.getFullYear() - nacimiento.getFullYear();
    const mes = hoy.getMonth() - nacimiento.getMonth();
    if (mes < 0 || (mes === 0 && hoy.getDate() < nacimiento.getDate())) {
      edad--;
    }
    return edad;
  };

  const getTemporadaActual = (futbolista) => {
    if (!futbolista.temporadas || futbolista.temporadas.length === 0) return null;

    // Usar la temporada activa dinámicamente
    if (temporadaActiva) {
      return futbolista.temporadas.find(t => t.temporada?.nombre === temporadaActiva.nombre) || futbolista.temporadas[0];
    }

    // Fallback: usar la primera temporada disponible
    return futbolista.temporadas[0];
  };

  // Función de filtrado
  const getFutbolistasFiltrados = () => {
    return futbolistas.filter(futbolista => {
      // Filtro por búsqueda
      const coincideBusqueda = busqueda === '' || 
        futbolista.nombre.toLowerCase().includes(busqueda.toLowerCase()) ||
        (futbolista.apodo && futbolista.apodo.toLowerCase().includes(busqueda.toLowerCase()));

      // Filtro por edad
      let coincideEdad = true;
      if (filtroEdad !== 'todos') {
        const edad = calcularEdad(futbolista.fecha_nacimiento);
        if (filtroEdad === 'benjamin' && (edad < 8 || edad > 10)) coincideEdad = false;
        if (filtroEdad === 'alevin' && (edad < 11 || edad > 12)) coincideEdad = false;
        if (filtroEdad === 'infantil' && (edad < 13 || edad > 14)) coincideEdad = false;
        if (filtroEdad === 'cadete' && (edad < 15 || edad > 16)) coincideEdad = false;
        if (filtroEdad === 'juvenil' && (edad < 17 || edad > 18)) coincideEdad = false;
      }

      return coincideBusqueda && coincideEdad;
    });
  };

  // Funciones de manejo
  const handleView = (id) => {
    navigate(`/tutor/futbolistas/${id}`);
  };

  const handleEdit = (id) => {
    navigate(`/tutor/futbolistas/${id}/editar`);
  };

  const handleGenerateCredentials = (id) => {
    navigate(`/tutor/futbolistas/${id}/credenciales`);
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este futbolista?')) {
      try {
        await futbolistaService.delete(id);
        setAlert({
          show: true,
          message: 'Futbolista eliminado exitosamente',
          variant: 'success'
        });
        loadFutbolistas();
      } catch (error) {
        console.error('Error al eliminar futbolista:', error);

        // Extraer mensaje específico del error
        let errorMessage = 'Error al eliminar el futbolista';

        if (error.response?.data?.message) {
          errorMessage = error.response.data.message;
        } else if (error.response?.status === 400) {
          errorMessage = 'No se puede eliminar el futbolista porque tiene inscripciones activas o saldo pendiente';
        }

        setAlert({
          show: true,
          message: errorMessage,
          variant: 'danger'
        });
      }
    }
  };

  // Renovar futbolista para la temporada activa
  const handleRenovar = async (futbolista) => {
    const ultimaTemp = futbolista.temporadas?.sort((a, b) => b.temporada_id - a.temporada_id)[0];
    const clubNombre = ultimaTemp?.club?.nombre || 'Sin club';
    const divisionNombre = ultimaTemp?.division ? `${ultimaTemp.division.competicion} (${ultimaTemp.division.federacion})` : 'Sin división';

    const confirmar = window.confirm(
      `🔄 Renovar a ${futbolista.nombre} para la temporada ${temporadaActiva?.nombre}\n\n` +
      `Se copiarán los datos de la temporada anterior:\n` +
      `• Club: ${clubNombre}\n` +
      `• División: ${divisionNombre}\n\n` +
      `Los ProPoints empezarán de 0 en la nueva temporada.\n\n` +
      `¿Confirmar renovación?`
    );

    if (!confirmar) return;

    try {
      await futbolistaService.renovarTemporada(futbolista.id);
      setAlert({ show: true, message: `✅ ${futbolista.nombre} renovado para ${temporadaActiva?.nombre}`, variant: 'success' });
      loadFutbolistas();
    } catch (error) {
      const msg = error.response?.data?.message || 'Error al renovar temporada';
      setAlert({ show: true, message: `❌ ${msg}`, variant: 'danger' });
    }
  };

  // Comprobar si un futbolista necesita renovación
  const necesitaRenovar = (futbolista) => {
    return temporadaActiva &&
      futbolista.temporadas &&
      !futbolista.temporadas.some(t => t.temporada?.id === temporadaActiva.id);
  };

  if (loading) {
    return (
      <TutorLayout title="Cargando..." subtitle="Obteniendo lista de futbolistas...">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
          <div style={{ textAlign: 'center' }}>
            <div className="spinner" style={{ marginBottom: '16px' }}></div>
            <p>Cargando futbolistas...</p>
          </div>
        </div>
      </TutorLayout>
    );
  }

  return (
    <TutorLayout title="Mis Futbolistas" subtitle="Gestiona a todos tus futbolistas registrados">
      {alert.show && (
        <div className={`alert alert-${alert.variant === 'danger' ? 'danger' : alert.variant === 'success' ? 'success' : 'info'}`} style={{ marginBottom: '20px' }}>
          {alert.message}
        </div>
      )}

      {/* Estadísticas mejoradas */}
      <div className="stats-grid" style={{ marginBottom: '30px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-icon" style={{ color: '#4352a9' }}>
              <FaUsers size={24} />
            </div>
            <h3>{futbolistas && Array.isArray(futbolistas) ? futbolistas.length : 0}</h3>
            <p>Total Futbolistas</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-icon" style={{ color: '#28a745' }}>
              <FaCalendarAlt size={24} />
            </div>
            <h3>{futbolistas.filter(f => getTemporadaActual(f)).length}</h3>
            <p>Activos {temporadaActiva?.nombre || 'Temporada Actual'}</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-icon" style={{ color: '#ffc107' }}>
              <FaTrophy size={24} />
            </div>
            <h3>{futbolistas.reduce((total, f) => {
              const temporada = getTemporadaActual(f);
              return total + (temporada?.puntos_propoint || 0);
            }, 0)}</h3>
            <p>ProPoints Total</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-icon" style={{ color: '#17a2b8' }}>
              <FaChartLine size={24} />
            </div>
            <h3>{Math.round(futbolistas.reduce((total, f) => {
              const temporada = getTemporadaActual(f);
              return total + (temporada?.puntos_propoint || 0);
            }, 0) / Math.max(futbolistas.filter(f => getTemporadaActual(f)).length, 1))}</h3>
            <p>Promedio ProPoints</p>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-content">
            <div className="stat-icon" style={{ color: '#4CAF50' }}>
              <FaCalendarAlt size={24} />
            </div>
            <h3>{futbolistas.reduce((total, f) => {
              const saldoFutbolista = f.inscripciones?.reduce((saldo, inscripcion) => {
                return saldo + (inscripcion.saldo_jornadas || 0);
              }, 0) || 0;
              return total + saldoFutbolista;
            }, 0)}</h3>
            <p>Jornadas Disponibles</p>
          </div>
        </div>
      </div>

      {/* Filtros y búsqueda */}
      <div className="admin-card" style={{ marginBottom: '20px' }}>
        <div className="card-content">
          <div style={{ display: 'flex', gap: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
            {/* Búsqueda */}
            <div style={{ flex: '1', minWidth: '250px' }}>
              <div style={{ position: 'relative' }}>
                <FaSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#666' }} />
                <input
                  type="text"
                  placeholder="Buscar por nombre o apodo..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '10px 10px 10px 40px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px'
                  }}
                />
              </div>
            </div>

            {/* Filtro por edad */}
            <div style={{ minWidth: '200px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaFilter style={{ color: '#666' }} />
                <select
                  value={filtroEdad}
                  onChange={(e) => setFiltroEdad(e.target.value)}
                  style={{
                    padding: '10px',
                    border: '1px solid #ddd',
                    borderRadius: '8px',
                    fontSize: '14px',
                    minWidth: '150px'
                  }}
                >
                  <option value="todos">Todas las edades</option>
                  <option value="benjamin">Benjamín (8-10)</option>
                  <option value="alevin">Alevín (11-12)</option>
                  <option value="infantil">Infantil (13-14)</option>
                  <option value="cadete">Cadete (15-16)</option>
                  <option value="juvenil">Juvenil (17-18)</option>
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fichas de futbolistas */}
      <div className="admin-card">
        <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h3>
            <FaUsers style={{ marginRight: '8px' }} />
            Lista de Futbolistas ({(() => {
              const filtrados = getFutbolistasFiltrados();
              return filtrados.length !== futbolistas.length ? 
                `${filtrados.length} de ${futbolistas.length}` : 
                futbolistas.length;
            })()})
          </h3>
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/tutor/futbolistas/nuevo')}
          >
            <FaPlus style={{ marginRight: '8px' }} />
            Nuevo Futbolista
          </button>
        </div>

        <div className="card-content">
          {futbolistas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 20px' }}>
              <FaUsers size={80} style={{ color: 'var(--text-secondary)', marginBottom: '16px' }} />
              <h4 style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>No tienes futbolistas registrados</h4>
              <p style={{ color: 'var(--text-secondary)', marginBottom: '20px' }}>
                Crea tu primer futbolista para comenzar a gestionar su participación en ProKids
              </p>
              <button
                className="btn btn-primary btn-lg"
                onClick={() => navigate('/tutor/futbolistas/nuevo')}
              >
                <FaPlus style={{ marginRight: '8px' }} />
                Crear Primer Futbolista
              </button>
            </div>
          ) : (
            <div>
              {/* Tabla de futbolistas */}
              {getFutbolistasFiltrados().length === 0 && (busqueda || filtroEdad !== 'todos') ? (
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <FaUsers size={60} style={{ color: 'var(--text-secondary)', marginBottom: '16px', opacity: 0.5 }} />
                  <h4 style={{ color: 'var(--text-secondary)', marginBottom: '8px' }}>
                    No se encontraron futbolistas
                  </h4>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    Intenta cambiar los criterios de búsqueda o filtros
                  </p>
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover" style={{ tableLayout: 'auto' }}>
                    <thead>
                      <tr>
                        <th style={{ padding: '12px 20px', minWidth: '200px' }}>Nombre</th>
                        <th style={{ padding: '12px 20px', minWidth: '100px' }}>Edad</th>
                        <th style={{ padding: '12px 20px', minWidth: '120px' }}>ProPoints</th>
                        <th style={{ padding: '12px 20px', minWidth: '120px' }}>Jornadas</th>
                        <th style={{ padding: '12px 20px', minWidth: '180px' }}>Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFutbolistasFiltrados().map(futbolista => {
                        const edad = calcularEdad(futbolista.fecha_nacimiento);
                        const temporada = getTemporadaActual(futbolista);

                        return (
                          <tr key={futbolista.id}>
                            <td style={{ padding: '12px 20px' }}>
                              <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{
                                  width: '40px',
                                  height: '40px',
                                  borderRadius: '50%',
                                  backgroundColor: '#4352a9',
                                  color: 'white',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  fontWeight: 'bold',
                                  marginRight: '12px'
                                }}>
                                  {futbolista.nombre.charAt(0).toUpperCase()}
                                </div>
                                <div>
                                  <div style={{ fontWeight: '600' }}>{futbolista.nombre}</div>
                                  {futbolista.apodo && (
                                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
                                      &ldquo;{futbolista.apodo}&rdquo;
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                            <td style={{ padding: '12px 20px' }}>{edad} años</td>
                            <td style={{ padding: '12px 20px' }}>
                              <span style={{
                                color: '#F57F17',
                                fontWeight: '600',
                                backgroundColor: 'rgba(255, 215, 0, 0.1)',
                                padding: '4px 8px',
                                borderRadius: '4px',
                                fontSize: '13px'
                              }}>
                                🌟 {temporada?.puntos_propoint || 0}
                              </span>
                            </td>
                            <td style={{ padding: '12px 20px' }}>
                              {(() => {
                                // Calcular el saldo total de jornadas de todas las inscripciones activas
                                const saldoTotal = futbolista.inscripciones?.reduce((total, inscripcion) => {
                                  return total + (inscripcion.saldo_jornadas || 0);
                                }, 0) || 0;

                                return (
                                  <span style={{
                                    color: saldoTotal > 0 ? '#4CAF50' : '#757575',
                                    fontWeight: '600',
                                    backgroundColor: saldoTotal > 0 ? 'rgba(76, 175, 80, 0.1)' : 'rgba(117, 117, 117, 0.1)',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '13px'
                                  }}>
                                    ⚽ {saldoTotal}
                                  </span>
                                );
                              })()}
                            </td>
                            <td style={{ padding: '12px 20px' }}>
                              <div style={{ display: 'flex', gap: '8px' }}>
                                <button
                                  className="btn btn-outline-primary btn-sm"
                                  onClick={() => handleView(futbolista.id)}
                                  title="Ver detalles"
                                >
                                  <FaEye />
                                </button>
                                <button
                                  className="btn btn-outline-secondary btn-sm"
                                  onClick={() => handleEdit(futbolista.id)}
                                  title="Editar"
                                >
                                  <FaEdit />
                                </button>
                                {necesitaRenovar(futbolista) && (
                                  <button
                                    className="btn btn-outline-warning btn-sm"
                                    onClick={() => handleRenovar(futbolista)}
                                    title={`Renovar para ${temporadaActiva?.nombre}`}
                                  >
                                    <FaSyncAlt />
                                  </button>
                                )}
                                <button
                                  className="btn btn-outline-info btn-sm"
                                  onClick={() => handleGenerateCredentials(futbolista.id)}
                                  title="Generar credenciales"
                                >
                                  <FaKey />
                                </button>
                                <button
                                  className="btn btn-outline-danger btn-sm"
                                  onClick={() => handleDelete(futbolista.id)}
                                  title="Eliminar"
                                >
                                  <FaTrash />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </TutorLayout>
  );
};

export default FutbolistaManagement;
